import React from 'react';
import PropTypes from 'prop-types';

/**
 * A React component to display a formatted phone number.
 * It strips non-numeric characters from the input string and formats it.
 * - For 10-digit numbers: (XXX) XXX-XXXX
 * - For 11-digit numbers starting with '1': +1 (XXX) XXX-XXXX
 * - Otherwise, displays the cleaned numeric string.
 *
 * @param {object} props - The component's props.
 * @param {string} [props.phoneNumber=''] - The raw phone number string to format.
 * @param {string} [props.className=''] - Optional CSS class name for the span.
 * @param {React.ReactNode} [props.fallback=null] - What to render if the phone number is empty or contains no digits.
                                                 If null, renders nothing.
 */
const FormattedPhoneNumber = ({ phoneNumber, className, fallback }) => {
  // Handle null, undefined, or non-string inputs gracefully.
  // If phoneNumber is already a number, convert it to a string.
  if (phoneNumber === null || phoneNumber === undefined) {
    return fallback;
  }

  const phoneNumberStr = String(phoneNumber);

  // Remove all non-numeric characters
  const cleaned = phoneNumberStr.replace(/\D/g, '');

  let formattedNumber = '';

  if (cleaned.length === 10) {
    // Format for 10-digit numbers: (XXX) XXX-XXXX
    const areaCode = cleaned.substring(0, 3);
    const centralOfficeCode = cleaned.substring(3, 6);
    const lineNumber = cleaned.substring(6, 10);
    formattedNumber = `(${areaCode}) ${centralOfficeCode}-${lineNumber}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // Format for 11-digit numbers starting with '1' (e.g., +1 XXX-XXX-XXXX)
    const areaCode = cleaned.substring(1, 4);
    const centralOfficeCode = cleaned.substring(4, 7);
    const lineNumber = cleaned.substring(7, 11);
    formattedNumber = `+1 (${areaCode}) ${centralOfficeCode}-${lineNumber}`;
  } else if (cleaned.length > 0) {
    // If not a 10-digit or 11-digit (starting with '1') number, display the cleaned version
    formattedNumber = cleaned;
  } else {
    // If cleaned is empty (original input was empty or had no digits)
    return fallback;
  }

  return <span className={className}>{formattedNumber}</span>;
};

FormattedPhoneNumber.propTypes = {
  /**
   * The raw phone number string (or number) to be formatted.
   */
  phoneNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Optional CSS class to apply to the wrapping span element.
   */
  className: PropTypes.string,
  /**
   * Fallback content to display if the phone number is empty or contains no digits.
   * Defaults to null (renders nothing).
   */
  fallback: PropTypes.node,
};

FormattedPhoneNumber.defaultProps = {
  phoneNumber: '',
  className: '',
  fallback: null,
};

// To use this component in your App.js or other components:
// 1. Import it: import FormattedPhoneNumber from './FormattedPhoneNumber'; (adjust path as needed)
// 2. Use it in JSX:
//    <FormattedPhoneNumber phoneNumber="919-123-4567" />
//    <FormattedPhoneNumber phoneNumber="(555) 555 5555" className="my-custom-phone-style" />
//    <FormattedPhoneNumber phoneNumber="18005551212" />
//    <FormattedPhoneNumber phoneNumber="12345" />
//    <FormattedPhoneNumber phoneNumber="" fallback={<em>Not available</em>} />

export default FormattedPhoneNumber;